import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { LenisProvider } from './hooks/useLenis';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { BookingModalProvider } from './contexts/BookingModalContext';

// Layout
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import MobileBottomBar from './components/layout/MobileBottomBar';
import WhatsAppButton from './components/common/WhatsAppButton';
import BookingModal from './components/common/BookingModal';

// Pages
import HomePage from './pages/HomePage';
import ProcessPage from './pages/ProcessPage';
import FactoryPage from './pages/FactoryPage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';

// Room Pages
import RoomPage from './pages/rooms/RoomPage';

// Component to handle route changes and cleanup GSAP
function RouteChangeHandler() {
  const location = useLocation();

  // Kill ScrollTriggers function
  const killAllScrollTriggers = () => {
    try {
      const allTriggers = ScrollTrigger.getAll();
      // Kill all triggers synchronously - this unpins any pinned elements
      allTriggers.forEach((trigger) => {
        try {
          trigger.kill(true); // Force kill immediately, don't wait for animation
        } catch (e) {
          // Ignore errors
        }
      });
    } catch (e) {
      // Ignore errors
    }
  };

  // Use useLayoutEffect for synchronous execution before React paints
  useLayoutEffect(() => {
    // Kill immediately when location changes (before React unmounts)
    killAllScrollTriggers();
    
    // Scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Intercept all navigation clicks to kill ScrollTriggers BEFORE navigation
  useEffect(() => {
    const handleClick = (e) => {
      // Check if this is an internal navigation link
      const link = e.target.closest('a[href]');
      if (link) {
        const href = link.getAttribute('href');
        // Check if it's an internal route (starts with / and not external protocol)
        if (href && href.startsWith('/') && !href.startsWith('//')) {
          // Kill ScrollTriggers immediately before navigation
          killAllScrollTriggers();
        }
      }
    };

    // Use capture phase to intercept before React Router handles it
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  // Separate effect for refresh after mount
  useEffect(() => {
    // Refresh ScrollTrigger after new components mount
    const timeoutId = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 150);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <LenisProvider>
      <Router>
        <BookingModalProvider>
          <RouteChangeHandler />
          <div className="min-h-screen bg-background-main overflow-x-hidden max-w-full">
            <Header />
            <main className="overflow-x-hidden max-w-full">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/process" element={<ProcessPage />} />
                <Route path="/factory" element={<FactoryPage />} />
                <Route path="/gallery" element={<GalleryPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/rooms/:roomType" element={<RoomPage />} />
              </Routes>
            </main>
            <Footer />
            <MobileBottomBar />
            <WhatsAppButton />
            <BookingModal />
          </div>
        </BookingModalProvider>
      </Router>
    </LenisProvider>
  );
}

export default App;
